import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useState } from 'react';

/**
 * CancelInviteDialog - Confirmation dialog for cancelling invite
 * 
 * Features:
 * - Warning message
 * - Shows recipient email
 * - Loading state during API call
 * - Error handling
 * - Destructive action styling
 */
const CancelInviteDialog = ({ open, onClose, invite, onConfirm }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleCancel = async () => {
    setLoading(true);
    setError(null);

    try {
      await onConfirm(invite._id);
      onClose();
    } catch (err) {
      setError(err.message || 'Davet iptal edilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  if (!invite) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <WarningAmberIcon color="error" />
          <Typography variant="h6" fontWeight="bold">
            Daveti İptal Et
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Typography variant="body1">
            <strong>{invite.email}</strong> adresine gönderilen davet iptal edilecek.
          </Typography>

          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2" fontWeight="medium">
              Bu işlem geri alınamaz!
            </Typography>
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              İptal edilen davet artık kullanılamayacak. Kullanıcıyı davet etmek için yeni bir davet göndermeniz gerekecek.
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="outlined"
        >
          Vazgeç
        </Button>
        <Button
          onClick={handleCancel}
          variant="contained"
          color="error"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : <WarningAmberIcon />}
        >
          {loading ? 'İptal Ediliyor...' : 'Evet, İptal Et'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

CancelInviteDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invite: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired
  }),
  onConfirm: PropTypes.func.isRequired
};

export default CancelInviteDialog;
